/* STEP: Profile & Service (v6 "DMS" — Direction, Mode & Service Type).
   Freight Direction is SINGLE-select (backend: one account code = one direction —
   FWD → type FORWARD, REV_NOQC/REV_QC → type RVP) · Shipment Value (HV/NHV) ·
   Payment Mode · grouped service-type CHIPS limited to two slots (first pick =
   Primary, second = Secondary) with a per-direction default pre-selected.
   Derives W.S direction/mode/type/dispatchMode/isCod/isPrepaid/qc for downstream. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import {useToast, cx} from '../../components'
import {Footer, scrollToFirstError} from './fieldKit'
import {DMS_DIRS, DMS_VAL, DMS_PAY, DMS_GROUPS} from '../data'
import {SERVICE_TYPES_BY_CODE} from '../../dummyData'

/* service-code options per (single) freight direction */
function svcOptions(dirKey) {
  if (dirKey === 'REV_QC') return ['REV_REG_QC', 'REV_LARGE_QC']
  if (dirKey === 'REV_NOQC') return ['REV_REG_NOQC', 'REV_LARGE_NOQC', 'HV_REVERSE_REG', 'HV_REVERSE_LARGE']
  return [
    'FWD_SURFACE_REG',
    'FWD_SURFACE_LARGE',
    'FWD_AIR_REG',
    'FWD_AIR_LARGE',
    'HV_FWD_REG',
    'HV_FWD_LARGE',
    'SDD_FWD_REG',
    'SDD_FWD_LARGE',
    'SC_NDD_FWD_REG',
    'SC_NDD_FWD_LARGE',
    'INTERCITY_NDD_REG',
    'INTERCITY_NDD_LARGE',
    'H2TO4',
    'SUB2H',
  ]
}
const svcLabel = (c) => c + ((SERVICE_TYPES_BY_CODE[c] || {}).name ? ' — ' + SERVICE_TYPES_BY_CODE[c].name : '')

/* sensible default primary per direction — pre-selected, ops can change it */
const DEFAULT_PRIMARY = {FWD: 'FWD_SURFACE_REG', REV_NOQC: 'REV_REG_NOQC', REV_QC: 'REV_REG_QC'}

/* DMS_GROUPS filtered to the codes valid for the chosen direction */
function svcGroups(dirKey) {
  const allowed = new Set(svcOptions(dirKey))
  return DMS_GROUPS.map(([g, pairs]) => [g, pairs.filter(([c]) => allowed.has(c))]).filter(
    ([, pairs]) => pairs.length > 0
  )
}

const DIR_ICON = {truck: LocalShippingOutlinedIcon, sync: SyncOutlinedIcon, checkcircle: CheckCircleOutlinedIcon}

const CSS = `
.p3sec{border-top:1px solid var(--border);padding-top:16px;margin-top:16px}.p3sec:first-of-type{border-top:0;padding-top:0;margin-top:0}
.p3lbl{display:flex;align-items:center;gap:8px;font-weight:800;font-size:13.5px;margin-bottom:10px}
.p3lbl .bdg{font-size:9.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;background:var(--primary-l);color:var(--primary);padding:2px 7px;border-radius:6px}
.p3g3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}@media(max-width:820px){.p3g3{grid-template-columns:1fr}}
.p3card{border:1px solid var(--border);border-radius:10px;padding:12px 13px;cursor:pointer;display:flex;gap:10px;align-items:flex-start;background:#fff;transition:border-color .15s,background .15s}
.p3card:hover{border-color:var(--primary)}.p3card.sel{border-color:var(--primary);background:var(--primary-l)}
.p3card.sel.rev{border-color:var(--warning);background:var(--warning-bg)}
.p3card .ic{width:32px;height:32px;border-radius:8px;background:var(--row-alt);display:flex;align-items:center;justify-content:center;color:var(--primary);flex:none}
.p3card.sel.rev .ic{color:var(--warning)}
.p3card .t{font-weight:700;font-size:12.5px}.p3card .d{font-size:11px;color:var(--neutral);margin-top:2px;line-height:1.4}
.p3card .tick{margin-left:auto;width:16px;height:16px;border-radius:5px;border:1.5px solid var(--border);flex:none}.p3card.sel .tick{background:var(--primary);border-color:var(--primary)}.p3card.sel.rev .tick{background:var(--warning);border-color:var(--warning)}
.p3card.radio .tick{border-radius:50%}
.p3row{display:grid;grid-template-columns:150px auto 1fr;gap:12px;align-items:center;padding:9px 0;border-bottom:1px dashed var(--border)}.p3row:last-child{border-bottom:0}
.p3row .nm{font-weight:700;font-size:12.5px}
.p3chk{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;cursor:pointer;margin-right:14px}
.p3codes .mono{font-size:10.5px;background:var(--primary-l);color:var(--primary);border-radius:5px;padding:2px 7px;margin:0 5px 4px 0;display:inline-block}
.p3grp{border:1px solid var(--border);border-radius:10px;padding:11px 13px;margin-bottom:10px}
.p3grp-h{display:flex;align-items:center;justify-content:space-between;margin-bottom:9px}
.p3grp-h .gn{font-size:10.5px;font-weight:800;letter-spacing:.06em;color:var(--neutral)}
.p3grp-h .gc{font-size:10.5px;font-weight:700;color:var(--primary)}
.stchip{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--border);border-radius:16px;padding:5px 11px;font-size:11.5px;font-weight:600;cursor:pointer;background:#fff;color:#374151;margin:0 7px 7px 0}
.stchip.on{border-color:var(--primary);background:var(--primary-l);color:var(--primary)}
.stchip .dot{width:8px;height:8px;border-radius:50%;border:1.5px solid #9CA3AF;flex:none}.stchip.on .dot{background:var(--primary);border-color:var(--primary)}
.p3leg{font-size:11px;color:var(--neutral);display:flex;gap:14px;margin-bottom:10px}.p3leg .d2{width:9px;height:9px;border-radius:50%;display:inline-block;margin-right:5px;vertical-align:-1px}
/* validation — these sections aren't .field, so the global .field.invalid rules
   don't reach them; style the invalid state here. Hidden message by default,
   shown red when the section is marked invalid, with a red label + badge. */
.p3sec .vmsg{display:none;font-size:11.5px;font-weight:600;color:var(--error);margin-top:8px}
.p3sec.invalid .vmsg{display:block}
.p3sec.invalid .p3lbl{color:var(--error)}
.p3sec.invalid .p3lbl .bdg{background:var(--error);color:#fff}
.p3sec.invalid .p3card:not(.sel){border-color:var(--error)}
.p3sec.invalid .stchip:not(.on){border-color:var(--error)}
`

export default function StepDms({W, dispatch, goBack, goNext}) {
  const toast = useToast()
  const S = W.S
  const dirKey = (S.freightDir || [])[0] || ''
  const primary = W.D.serviceTypePrimary || ''
  const secondary = W.D.serviceTypeSecondary || ''
  const [inv, setInv] = useState({})

  const clearInv = (k) => setInv((p) => ({...p, [k]: false}))

  /* SINGLE-select: switching direction invalidates previously chosen service types */
  const setDir = (key) => {
    if (key === dirKey) return
    dispatch({type: 'FIELD', slice: 'S', key: 'freightDir', value: [key]})
    const def = DEFAULT_PRIMARY[key] || ''
    dispatch({
      type: 'PATCH',
      slice: 'D',
      value: {serviceTypePrimary: def, serviceTypeSecondary: '', serviceTypes: def ? [def] : []},
    })
    clearInv('freightDir')
    clearInv('serviceTypes')
  }

  const toggleSvc = (code) => {
    let p = primary
    let sec = secondary
    if (code === primary) {
      p = secondary // deselecting the primary promotes the secondary
      sec = ''
    } else if (code === secondary) {
      sec = ''
    } else if (!p) {
      p = code
    } else if (!sec) {
      sec = code
    } else {
      toast('Only a primary + secondary service type are supported — deselect one first.', 'warning')
      return
    }
    dispatch({
      type: 'PATCH',
      slice: 'D',
      value: {serviceTypePrimary: p, serviceTypeSecondary: sec, serviceTypes: [p, sec].filter(Boolean)},
    })
    clearInv('serviceTypes')
  }

  const setVal = (v) => {
    dispatch({type: 'FIELD', slice: 'S', key: 'shipmentValue', value: v})
    clearInv('shipmentValue')
  }
  const setPay = (v) => {
    dispatch({type: 'FIELD', slice: 'S', key: 'paymentMode', value: v})
    clearInv('paymentMode')
  }

  const onNext = () => {
    const e = {}
    if (!dirKey) e.freightDir = true
    if (!S.shipmentValue) e.shipmentValue = true
    if (!S.paymentMode) e.paymentMode = true
    if (!primary) e.serviceTypes = true
    setInv(e)
    scrollToFirstError()
    if (Object.keys(e).length) return
    // derive downstream + backend fields (one code = one direction)
    const ST = [primary, secondary].filter(Boolean)
    const direction = dirKey === 'FWD' ? 'Forward' : 'Reverse'
    const mode = ST.some((c) => /SURFACE/.test(c)) ? 'Surface' : ST.some((c) => /AIR/.test(c)) ? 'Air' : 'Surface'
    dispatch({
      type: 'PATCH',
      slice: 'S',
      value: {
        direction,
        mode,
        type: dirKey === 'FWD' ? 'FORWARD' : 'RVP',
        dispatchMode: mode === 'Air' ? 'AIR' : 'SURFACE',
        isCod: S.paymentMode === 'COD' || S.paymentMode === 'BOTH',
        isPrepaid: S.paymentMode === 'PREPAID' || S.paymentMode === 'BOTH',
        qc: dirKey === 'REV_QC',
      },
    })
    dispatch({type: 'FIELD', slice: 'D', key: 'serviceTypes', value: ST})
    goNext()
  }

  return (
    <div className='card card-pad wz-wide' style={{maxWidth: 1400}}>
      <style>{CSS}</style>
      <div className='row center gap10' style={{marginBottom: 2, display: 'flex', alignItems: 'center', gap: 10}}>
        <span className='badge b-info'>Operating Profile + Service Type</span>
      </div>
      <h3 style={{margin: '8px 0 4px'}}>Profile &amp; Service</h3>
      <p className='sub' style={{margin: '0 0 4px'}}>
        Freight direction (one per account code), shipment value, payment mode, and the primary / secondary service types.
      </p>

      {/* Freight Direction (single-select — one account code carries one direction) */}
      <div className={cx('p3sec', inv.freightDir && 'invalid')} data-field='freightDir'>
        <div className='p3lbl'>
          🧭 Freight Direction <span className='bdg'>Single-Select · Required</span>
        </div>
        <div className='p3g3'>
          {DMS_DIRS.map((o) => {
            const sel = dirKey === o[0]
            const Icon = DIR_ICON[o[3]] || LocalShippingOutlinedIcon
            return (
              <div key={o[0]} className={cx('p3card', 'radio', sel && 'sel', o[4] && 'rev')} onClick={() => setDir(o[0])}>
                <span className='ic'>
                  <Icon style={{fontSize: 17}} />
                </span>
                <div>
                  <div className='t'>{o[1]}</div>
                  <div className='d'>{o[2]}</div>
                </div>
                <span className='tick' />
              </div>
            )
          })}
        </div>
        <div className='vmsg' style={{marginTop: 6}}>
          Select a freight direction.
        </div>
      </div>

      {/* Shipment Value */}
      <div className={cx('p3sec', inv.shipmentValue && 'invalid')} data-field='shipmentValue'>
        <div className='p3lbl'>
          💎 Shipment Value Type <span className='bdg'>Required</span>
        </div>
        <div className='p3g3' style={{gridTemplateColumns: '1fr 1fr'}}>
          {DMS_VAL.map((o) => {
            const sel = S.shipmentValue === o[0]
            return (
              <div key={o[0]} className={cx('p3card', 'radio', sel && 'sel')} onClick={() => setVal(o[0])}>
                <span className='ic'>
                  <AttachMoneyOutlinedIcon style={{fontSize: 17}} />
                </span>
                <div>
                  <div className='t'>
                    {o[1]} ({o[0]})
                  </div>
                  <div className='d'>{o[2]}</div>
                </div>
                <span className='tick' />
              </div>
            )
          })}
        </div>
        <div className='vmsg' style={{marginTop: 6}}>
          Select High Value or Non-High Value.
        </div>
      </div>

      {/* Payment Mode */}
      <div className={cx('p3sec', inv.paymentMode && 'invalid')} data-field='paymentMode'>
        <div className='p3lbl'>
          💳 Payment Mode <span className='bdg'>Required</span>
        </div>
        <div className='p3g3'>
          {DMS_PAY.map((o) => {
            const sel = S.paymentMode === o[0]
            return (
              <div key={o[0]} className={cx('p3card', 'radio', sel && 'sel')} onClick={() => setPay(o[0])}>
                <span className='ic'>
                  <AttachMoneyOutlinedIcon style={{fontSize: 17}} />
                </span>
                <div>
                  <div className='t'>{o[1]}</div>
                  <div className='d'>{o[2]}</div>
                </div>
                <span className='tick' />
              </div>
            )
          })}
        </div>
        <div className='vmsg' style={{marginTop: 6}}>
          Select a payment mode.
        </div>
      </div>

      {/* Service Types — Primary (required) + Secondary (optional) */}
      <div className={cx('p3sec', inv.serviceTypes && 'invalid')} data-field='serviceTypes'>
        <div className='p3lbl'>
          🧩 Service Types <span className='bdg'>Aligned with Freight Direction</span>
        </div>
        {!dirKey ? (
          <div className='sub' style={{padding: '6px 0 2px'}}>
            Select a freight direction first — the matching service types will appear here.
          </div>
        ) : (
          <>
            <div className='p3leg'>
              <span>First pick = <b>Primary</b> (required) · second pick = <b>Secondary</b> (optional) · click again to deselect.</span>
            </div>
            {svcGroups(dirKey).map(([g, pairs]) => (
              <div key={g} className='p3grp'>
                <div className='p3grp-h'>
                  <span className='gn'>{g}</span>
                  <span className='gc'>
                    {pairs.filter(([c]) => c === primary || c === secondary).length > 0 &&
                      pairs.filter(([c]) => c === primary || c === secondary).length + ' selected'}
                  </span>
                </div>
                {pairs.map(([c, lbl]) => {
                  const role = c === primary ? 'PRIMARY' : c === secondary ? 'SECONDARY' : ''
                  return (
                    <span
                      key={c}
                      className={cx('stchip', role && 'on')}
                      title={svcLabel(c)}
                      onClick={() => toggleSvc(c)}
                    >
                      <span className='dot' />
                      {lbl}
                      <span style={{fontSize: 9.5, color: 'var(--neutral)'}} className='mono'>
                        {c}
                      </span>
                      {role && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: '.05em',
                            background: role === 'PRIMARY' ? 'var(--primary)' : 'var(--primary-l)',
                            color: role === 'PRIMARY' ? '#fff' : 'var(--primary)',
                            borderRadius: 5,
                            padding: '1px 6px',
                          }}
                        >
                          {role}
                        </span>
                      )}
                    </span>
                  )
                })}
              </div>
            ))}
          </>
        )}
        <div className='vmsg'>Select the primary service type.</div>
      </div>

      <Footer step={W.step} onBack={goBack} onNext={onNext} />
    </div>
  )
}

StepDms.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
